/**
 * Le contrat de la salle du tuteur : ce que les crochets de l'écran se passent. L'écran
 * (`TutorLiveScreen`) ne fait que les composer, zone par zone, sur `<TutorLivePage>` :
 *
 *   useTutorLiveRoom          → la salle (le moteur commun, les effets du tuteur, la vidéo, l'alerte)
 *   useTutorLiveSelection     → l'élève regardé, l'activité ouverte, le profil, le chat
 *   useTutorLivePresentation  → « Présenter » : le partage recadré, ou le centre seul sans recadrage
 *   useTutorLiveStudents      → la pellicule, les modes, les mains, l'objectif, le profil
 *   useTutorLiveCenter        → l'activité au centre (énoncé, rendu, quiz, tableau, dessin, menu)
 *   useTutorLiveFeed          → le fil (un seul, filtré sur l'élève regardé) et la génération
 *   useTutorLiveChrome        → la barre, « Plus », le chat, l'aide, la fin
 *
 * Chaque crochet rend sa zone, et ce qu'il pose par-dessus la salle (`TutorLiveLayer`) : l'écran les
 * range par priorité, une boîte à la fois, comme l'ancien live n'en montrait qu'une.
 */
import type React from "react"
import { type View } from "react-native"

import { type ChampionName } from "../../../../../../design-system/atoms/Avatar/ChampionAvatar"
import { type LiveRoom } from "../../../../../../engine/live/room/useLiveRoom"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"
import { type UILiveParticipant } from "../../../types/studentLive"
import { type UITutorLiveNotice, type UITutorLiveRoomKind } from "../../../types/tutorLive"
import {
    type UITutorLiveDialog,
    type UITutorLiveSnackbar,
} from "../../../../../../design-system/templates/TutorLiveOverlays/TutorLiveOverlays"

/** La salle, telle que la lit `useTutorLiveRoom`. */
export type TutorLiveRoomContext = {
    /** La séance du live (`liveMode.liveSessionId`). */
    liveSessionId: number | null
    /** Le moteur commun ; `null` avant le premier rendu de la salle. */
    live: LiveRoom | null
    /** La salle est connectée (le SDK et le relevé). */
    connected: boolean
    me: UILiveParticipant
    /** Les autres tuteurs de la séance connectés (un second tuteur, un remplaçant, l'équipe) : une tuile chacun. */
    coTutors: UILiveParticipant[]
    roomKind: UITutorLiveRoomKind
    /** L'instant où le live a commencé (ms) : le chrono. */
    startedAt: number
    /** À la place de la salle : la connexion, la reprise, personne. */
    notice?: UITutorLiveNotice
    onRetryConnection?: () => void
    onCancelConnection?: () => void
    /** La vraie piste d'un participant (`me.id` pour la mienne), sa caméra ou son partage. */
    renderVideo?: (participantId: string) => React.ReactNode
    /** Le prénom du champion, lu du monde de jeu de l'espace (jamais écrit en dur). */
    helperName: string
    /** Le champion lui-même (sa clé) : sa tête et son prénom signent la session et la pellicule. */
    helper: ChampionName
    telemetry: EngineTelemetry
    /** Ce que la salle pose par-dessus elle : plusieurs onglets, le live suivant, les permissions. */
    layer: TutorLiveLayer
    /**
     * Ce que l'écran monte à côté de la page, sans rien dessiner : CallKeep (l'appel système du natif,
     * `CallKeepManagerM`, comme l'ancien `LiveContainerV3`).
     */
    mounts?: React.ReactNode
    /**
     * Une notification de message de cette séance, touchée (natif) : le chat s'ouvre sur son fil
     * (`"group"` : « Tous », sinon l'id de l'élève). `seq` change à chaque toucher : l'écran appelle
     * `selection.openChat(channel)` une fois par `seq`.
     */
    chatRequest?: { channel: string; seq: number } | null
}

/** L'élève regardé, l'activité ouverte, le profil, le chat : l'état partagé des zones. */
export type TutorLiveSelection = {
    selectedId?: string
    select: (studentId: string | undefined) => void
    /**
     * Toucher un élève (sa carte de la pellicule, sa case de la mosaïque, sa bulle au téléphone, le rail) : le
     * regarder, et montrer au centre ce qu'il fait, à chaque fois, même s'il était déjà regardé (daily du 04/11 :
     * le toucher ouvrait la visio). `looked` change à chaque toucher.
     */
    look: (studentId: string) => void
    looked: { studentId: string; nonce: number } | null
    openActivity: { studentId: string; activityId: string } | null
    openActivityFor: (studentId: string, activityId: string) => void
    closeActivity: () => void
    profileId?: string
    openProfile: (studentId: string) => void
    closeProfile: () => void
    chat: { open: boolean; channel?: string }
    openChat: (channel?: string) => void
    closeChat: () => void
}

/** « Présenter » : ce qui est partagé, et si l'écran du tuteur se réduit au centre (sans recadrage). */
export type TutorLivePresentation = {
    on: boolean
    pending: boolean
    /** La capture est recadrée sur le centre (`CropTarget`) ; `false` : l'onglet entier, d'où `centerOnly`. */
    cropped: boolean
    /** Sans recadrage, la page ne montre que le centre tant que la présentation dure (tech-derisk, point 3). */
    centerOnly: boolean
    /** La région que le partage recadre : le centre (`TutorLiveCenter.shareRegionRef`). */
    shareRegionRef: React.RefObject<View>
    start: () => void
    stop: () => void
    /** Le partage d'écran de la barre (sans recadrage, comme l'ancien bouton). */
    screenShare: { on: boolean; pending: boolean; onToggle: () => void } | null
}

/**
 * Ce qu'un crochet pose par-dessus la salle. `priority` : la plus haute l'emporte, une à la fois.
 *
 * `dialogAutomatic` / `snackbarAutomatic` : la surface s'ouvre seule (un minuteur, le relevé, un message de
 * salle), sans geste du masteur, et peut porter un prénom. Pendant une présentation, recadrée ou non, elle
 * attend que la présentation finisse : la boîte s'ouvre au milieu de l'écran et la snackbar au-dessus de la
 * barre, toutes deux par-dessus le centre, donc dans ce qui part chez les élèves (le recadrage, `cropTo`,
 * découpe l'onglet tel qu'il est peint, ce qui couvre le centre compris). Une surface ouverte par un geste du
 * masteur reste montrée.
 */
export type TutorLiveLayer = {
    dialog?: { value: UITutorLiveDialog; onConfirm: () => void; onCancel?: () => void; onDismiss?: () => void } | null
    dialogPriority?: number
    dialogAutomatic?: boolean
    snackbar?: { value: UITutorLiveSnackbar; onDismiss: () => void } | null
    snackbarAutomatic?: boolean
    sheet?: React.ReactNode
}

/** Un id d'élève tel que le serveur et les tranches de `liveMode` l'écrivent : un nombre quand c'en est un. */
export function rawId(id: string | number): number | string {
    return typeof id === "string" && /^\d+$/.test(id) ? Number(id) : id
}

/**
 * La première boîte, la première feuille, la première snackbar, par priorité décroissante. `presenting` : une
 * présentation est en cours ou part (`presentation.on || presentation.pending`), recadrée ou non ; les surfaces
 * automatiques attendent (elles restent dans leur crochet, et reviennent à l'arrêt de la présentation).
 */
export function pickLayer(layers: TutorLiveLayer[], options: { presenting?: boolean } = {}): TutorLiveLayer {
    const quiet = options.presenting === true
    const withDialog = layers
        .filter((layer) => layer.dialog != null && !(quiet && layer.dialogAutomatic === true))
        .sort((a, b) => (b.dialogPriority ?? 0) - (a.dialogPriority ?? 0))
    return {
        dialog: withDialog[0]?.dialog ?? null,
        snackbar:
            layers.find((layer) => layer.snackbar != null && !(quiet && layer.snackbarAutomatic === true))?.snackbar ??
            null,
        sheet: layers.find((layer) => layer.sheet != null)?.sheet,
    }
}
